import { useFormik } from 'formik'
import { FC, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import * as Yup from 'yup'
import {
  ButtonType,
  DocumentoPessoaLabels,
  DocumentoPessoaType,
  formatDateToPtBr,
  IconPaths,
  IconSize,
  OptionsHelper,
  showAlert,
  showConfirmAlert,
  showError,
} from '_metronic/helpers'
import {
  Button,
  ButtonIcon,
  FormDateInput,
  FormInput,
  FormSelect,
  InfoAlert,
  IOption,
  LoadingIndicator,
} from 'app/components'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  DocumentoPessoa,
  DocumentoPessoaDtoUpdate,
  DocumentosPessoaRequests,
  TIPOS_DOCUMENTO_LISTA,
} from './_models'

type Props = {
  /** Id do funcionário ou paciente dono da lista; ausente enquanto o cadastro não foi salvo. */
  ownerId?: number
  /** Distingue o cache de funcionário do de paciente (`funcionario` | `paciente`). */
  recurso: string
  requests: DocumentosPessoaRequests
  readOnly?: boolean
  /** Avisa o modal pai sobre os documentos atuais (regra de identificador do formulário principal). */
  onChange?: (documentos: DocumentoPessoa[]) => void
}

const SEM_DOCUMENTOS: DocumentoPessoa[] = []

/** Campos exibidos por tipo; o número vale para todos. */
const CAMPOS_POR_TIPO: Record<string, string[]> = {
  [DocumentoPessoaType.Rg]: ['complemento', 'orgao', 'uf', 'expedicao'],
  [DocumentoPessoaType.Passaporte]: ['paisEmissor', 'expedicao', 'validade'],
  [DocumentoPessoaType.Ctps]: ['serie', 'uf', 'expedicao'],
  [DocumentoPessoaType.BilheteIdentidadeAngola]: ['expedicao', 'validade'],
}

type Valores = {
  tipo?: IOption<DocumentoPessoaType>
  numero: string
  complemento: string
  serie: string
  orgao: string
  uf?: IOption<string>
  expedicao: string
  validade: string
  paisEmissor?: IOption<number>
}

const valoresDe = (documento?: DocumentoPessoa): Valores => ({
  tipo: documento
    ? { label: DocumentoPessoaLabels[documento.tipo], value: documento.tipo }
    : undefined,
  numero: documento?.numero || '',
  complemento: documento?.complemento || '',
  serie: documento?.serie || '',
  orgao: documento?.orgao || '',
  uf: documento?.uf ? { label: documento.uf, value: documento.uf } : undefined,
  expedicao: documento?.expedicao || '',
  validade: documento?.validade || '',
  paisEmissor: documento?.paisEmissor
    ? { label: documento.paisEmissor.nome, value: documento.paisEmissor.id }
    : undefined,
})

export const DocumentosPessoaForm: FC<Props> = ({
  ownerId,
  recurso,
  requests,
  readOnly = false,
  onChange,
}) => {
  const { t } = useT()
  const queryClient = useQueryClient()
  const chave = useMemo(() => ['documentos-pessoa', recurso, ownerId], [recurso, ownerId])

  const { data, isFetching } = useQuery(chave, () => requests.listar(ownerId as number), {
    enabled: !!ownerId,
    refetchOnWindowFocus: false,
  })
  // Referência estável: um `[]` novo a cada render dispararia o `onChange` do pai em loop.
  const documentos = data ?? SEM_DOCUMENTOS

  useEffect(() => {
    onChange?.(documentos)
  }, [documentos, onChange])

  // `null` = editor fechado; `undefined` dentro do objeto = documento novo.
  const [editando, setEditando] = useState<{ documento?: DocumentoPessoa } | null>(null)

  const tiposDisponiveis = useMemo(
    () =>
      TIPOS_DOCUMENTO_LISTA.filter((tipo) => !documentos.some((doc) => doc.tipo === tipo)).map(
        (tipo) => ({ label: DocumentoPessoaLabels[tipo], value: tipo })
      ),
    [documentos]
  )

  const formik = useFormik<Valores>({
    initialValues: valoresDe(),
    validationSchema: Yup.object().shape({
      tipo: Yup.object().nullable().required(t('documento_pessoa.tipo_obrigatorio')),
      numero: Yup.string()
        .trim()
        .max(30, t('documento_pessoa.max', { max: 30 }))
        .required(t('documento_pessoa.numero_obrigatorio')),
      complemento: Yup.string().max(20, t('documento_pessoa.max', { max: 20 })),
      serie: Yup.string().max(10, t('documento_pessoa.max', { max: 10 })),
      orgao: Yup.string().max(10, t('documento_pessoa.max', { max: 10 })),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!ownerId || !values.tipo) return
      const campos = CAMPOS_POR_TIPO[values.tipo.value] || []
      const usa = (campo: string) => campos.includes(campo)
      const dto: DocumentoPessoaDtoUpdate = {
        numero: values.numero.trim(),
        complemento: usa('complemento') ? values.complemento || undefined : undefined,
        serie: usa('serie') ? values.serie || undefined : undefined,
        orgao: usa('orgao') ? values.orgao || undefined : undefined,
        uf: usa('uf') ? values.uf?.value : undefined,
        expedicao: usa('expedicao') ? values.expedicao || undefined : undefined,
        validade: usa('validade') ? values.validade || undefined : undefined,
        paisEmissor: usa('paisEmissor') ? values.paisEmissor?.value : undefined,
      }
      setSubmitting(true)
      try {
        if (editando?.documento) {
          await requests.atualizar(ownerId, editando.documento.id, dto)
        } else {
          await requests.criar(ownerId, { ...dto, tipo: values.tipo.value })
        }
        await queryClient.invalidateQueries(chave)
        setEditando(null)
        showAlert({ title: t('documento_pessoa.salvo'), icon: 'success' })
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  /** Reset explícito: os valores iniciais de "novo" não mudam entre aberturas, então reinicializar não basta. */
  const abrirEditor = (documento?: DocumentoPessoa) => {
    formik.resetForm({ values: valoresDe(documento) })
    setEditando({ documento })
  }

  const excluir = async (documento: DocumentoPessoa) => {
    if (!ownerId) return
    const confirmado = await showConfirmAlert({
      title: t('documento_pessoa.excluir_titulo'),
      text: `${DocumentoPessoaLabels[documento.tipo]} ${documento.numero}`,
    })
    if (!confirmado) return
    try {
      await requests.excluir(ownerId, documento.id)
      await queryClient.invalidateQueries(chave)
    } catch (error) {
      showError(error)
    }
  }

  if (!ownerId) {
    return (
      <InfoAlert variant='info' title={t('documento_pessoa.titulo')}>
        {t('documento_pessoa.salve_antes')}
      </InfoAlert>
    )
  }

  const tipoSelecionado = formik.values.tipo?.value
  const campos = tipoSelecionado ? CAMPOS_POR_TIPO[tipoSelecionado] || [] : []
  const mostra = (campo: string) => campos.includes(campo)
  const idCampo = (campo: string) => `documento-${recurso}-${campo}`

  return (
    <div className='mb-3'>
      <div className='table-responsive'>
        <table className='table table-row-bordered align-middle gs-0 gy-2'>
          <caption className='visually-hidden'>{t('documento_pessoa.titulo')}</caption>
          <thead>
            <tr className='fw-bold text-muted'>
              <th scope='col'>{t('documento_pessoa.tipo')}</th>
              <th scope='col'>{t('numero.label')}</th>
              <th scope='col'>{t('documento_pessoa.orgao_uf')}</th>
              <th scope='col'>{t('documento_pessoa.emissao')}</th>
              <th scope='col'>{t('documento_pessoa.validade')}</th>
              {!readOnly && (
                <th scope='col' className='text-end'>
                  {t('documento_pessoa.acoes')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {documentos.length === 0 && (
              <tr>
                <td colSpan={readOnly ? 5 : 6} className='text-muted'>
                  {isFetching ? <LoadingIndicator /> : t('documento_pessoa.vazio')}
                </td>
              </tr>
            )}
            {documentos.map((documento) => {
              const rotulo = `${DocumentoPessoaLabels[documento.tipo]} ${documento.numero}`
              return (
                <tr key={documento.id}>
                  <td>{DocumentoPessoaLabels[documento.tipo]}</td>
                  <td>
                    {documento.numero}
                    {documento.complemento ? ` (${documento.complemento})` : ''}
                    {documento.serie ? ` / ${documento.serie}` : ''}
                  </td>
                  <td>
                    {[documento.orgao, documento.uf, documento.paisEmissor?.nome]
                      .filter(Boolean)
                      .join(' / ') || '-'}
                  </td>
                  <td>{documento.expedicao ? formatDateToPtBr(documento.expedicao) : '-'}</td>
                  <td>{documento.validade ? formatDateToPtBr(documento.validade) : '-'}</td>
                  {!readOnly && !TIPOS_DOCUMENTO_LISTA.includes(documento.tipo) && (
                    // CPF e CNS aparecem na grade, mas são editados no formulário principal.
                    <td className='text-end text-muted fs-7'>
                      {t('documento_pessoa.no_cadastro')}
                    </td>
                  )}
                  {!readOnly && TIPOS_DOCUMENTO_LISTA.includes(documento.tipo) && (
                    <td className='text-end text-nowrap'>
                      <ButtonIcon
                        className='p-0 me-2'
                        icon={IconPaths.Pen}
                        iconSize={IconSize.Small}
                        ariaLabel={`${t('common.edit')} ${rotulo}`}
                        onClick={() => abrirEditor(documento)}
                        disabled={formik.isSubmitting}
                      />
                      <ButtonIcon
                        className='m-0 p-0'
                        icon={IconPaths.Trash}
                        iconSize={IconSize.Small}
                        ariaLabel={`${t('common.delete')} ${rotulo}`}
                        onClick={() => excluir(documento)}
                        disabled={formik.isSubmitting}
                      />
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && !editando && tiposDisponiveis.length > 0 && (
        <div className='d-flex justify-content-end'>
          <Button className='btn-primary' size='btn-sm' onClick={() => abrirEditor()}>
            {t('documento_pessoa.adicionar')}
          </Button>
        </div>
      )}

      {!readOnly && editando && (
        // Sem <form>: o componente vive dentro do formulário do modal, e form aninhado é inválido.
        <div
          className='border rounded p-4 mt-3'
          role='group'
          aria-label={t('documento_pessoa.titulo')}
        >
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='tipo'
                inputId={idCampo('tipo')}
                label={t('documento_pessoa.tipo')}
                options={tiposDisponiveis}
                isClearable={false}
                isDisabled={!!editando.documento || formik.isSubmitting}
                isRequired
              />
            </div>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='numero'
                entity='numero'
                isDisabled={formik.isSubmitting}
                isRequired
              />
            </div>
          </div>
          <div className='row'>
            {mostra('complemento') && (
              <div className='col-md-6 mb-3'>
                <FormInput formik={formik} name='complemento' entity='complemento' />
              </div>
            )}
            {mostra('serie') && (
              <div className='col-md-6 mb-3'>
                <FormInput formik={formik} name='serie' label={t('documento_pessoa.serie')} />
              </div>
            )}
            {mostra('orgao') && (
              <div className='col-md-6 mb-3'>
                <FormInput formik={formik} name='orgao' label={t('documento_pessoa.orgao')} />
              </div>
            )}
            {mostra('uf') && (
              <div className='col-md-6 mb-3'>
                <FormSelect
                  formik={formik}
                  name='uf'
                  inputId={idCampo('uf')}
                  entity='estado'
                  fetchOptions={OptionsHelper.getOptionsEstadosUf}
                  isDisabled={formik.isSubmitting}
                />
              </div>
            )}
            {mostra('paisEmissor') && (
              <div className='col-md-6 mb-3'>
                <FormSelect
                  formik={formik}
                  name='paisEmissor'
                  inputId={idCampo('paisEmissor')}
                  label={t('documento_pessoa.pais_emissor')}
                  fetchOptions={OptionsHelper.getOptionsPaises}
                  isDisabled={formik.isSubmitting}
                />
              </div>
            )}
            {mostra('expedicao') && (
              <div className='col-md-6 mb-3'>
                <FormDateInput
                  formik={formik}
                  name='expedicao'
                  label={t('documento_pessoa.emissao')}
                  maxDate={new Date()}
                  isDisabled={formik.isSubmitting}
                />
              </div>
            )}
            {mostra('validade') && (
              <div className='col-md-6 mb-3'>
                <FormDateInput
                  formik={formik}
                  name='validade'
                  label={t('documento_pessoa.validade')}
                  isDisabled={formik.isSubmitting}
                />
              </div>
            )}
          </div>
          <div className='d-flex justify-content-end gap-2'>
            <Button
              type={ButtonType.Button}
              className='btn-secondary'
              size='btn-sm'
              onClick={() => setEditando(null)}
              disabled={formik.isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type={ButtonType.Button}
              className='btn-primary'
              size='btn-sm'
              onClick={() => formik.submitForm()}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? <LoadingIndicator /> : t('documento_pessoa.salvar')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
